import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaInstagram, FaExternalLinkAlt, FaCalendarAlt, FaRoad, FaGasPump, FaCogs, FaUsers, FaShieldAlt } from 'react-icons/fa';
import LoadingSpinner from '../Components/LoadingSpinner';
import { useCar } from '../stores/CarContext';
import { formatPrice } from '../utils/formatters';
import SEO from '../Components/SEO';
import { buildBreadcrumbSchema, buildCarProductSchema, SITE_NAME, SOCIAL_LINKS } from '../seoConfig';

const CarDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetchCarDetails } = useCar();

    const [car, setCar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const load = async () => {
            setLoading(true);
            const res = await fetchCarDetails(id);
            if (res.success) {
                setCar(res.data);
                setError(null);
            } else {
                setError(res.error);
            }
            setLoading(false);
        };
        load();
        // fetchCarDetails is stable (useCallback); only reload when route id changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center pt-20">
                <LoadingSpinner />
            </div>
        );
    }

    if (error || !car) {
        return (
            <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-6 p-10 text-center pt-20">
                <p className="font-display text-3xl text-white tracking-widest">Veículo não encontrado</p>
                <button onClick={() => navigate('/automoveis')} className="btn-primary">
                    <FaArrowLeft size={10} /> Voltar ao stock
                </button>
            </div>
        );
    }

    const isSold = Number(car.vendido) === 1;
    const isSoon = Number(car.brevemente) === 1;
    const statusLabel = isSold ? 'Vendido' : isSoon ? 'Em Breve' : 'Disponível';
    const statusClass = isSold
        ? 'border-white/20 text-white/50'
        : isSoon
            ? 'border-amber-500/40 text-amber-400'
            : 'border-red/40 text-red';

    const instagramUrl = car.instagramShortcode
        ? `https://www.instagram.com/p/${car.instagramShortcode}/`
        : car.linkinstagram || SOCIAL_LINKS.instagram;

    const contactMessage = encodeURIComponent(`Olá, estou interessado no veículo: ${car.titulo}`);

    const specs = [
        { icon: FaCalendarAlt, label: 'Ano', value: car.ano },
        { icon: FaRoad, label: 'Quilometragem', value: car.kms ? `${new Intl.NumberFormat('pt-PT').format(Number(String(car.kms).replace(/\D/g, '')) || car.kms)} km` : null },
        { icon: FaGasPump, label: 'Combustível', value: car.combustivel },
        { icon: FaCogs, label: 'Caixa', value: car.caixa_velocidades },
        { icon: FaUsers, label: 'Lotação', value: car.locacao ? `${car.locacao} lugares` : null },
        { icon: FaShieldAlt, label: 'Garantia', value: car.garantia_meses ? `${car.garantia_meses} meses` : null },
    ].filter((s) => s.value && s.value !== '—');

    return (
        <div className="bg-black text-white min-h-screen overflow-x-hidden">
            <SEO
                title={car.titulo}
                description={`${car.marca} ${car.titulo} — ${car.preco ? formatPrice(car.preco) : `disponível na ${SITE_NAME}`}.`}
                path={`/automoveis/${id}`}
                image={car.thumb_url || undefined}
                imageAlt={car.titulo}
                type="product"
                structuredData={[
                    buildCarProductSchema(car, id),
                    buildBreadcrumbSchema([
                        { name: 'Início', path: '/' },
                        { name: 'Stock', path: '/automoveis' },
                        { name: car.titulo, path: `/automoveis/${id}` },
                    ]),
                ].filter(Boolean)}
            />

            <div className="border-b border-white/[0.06] pt-24 pb-4 px-5 lg:px-12">
                <div className="max-w-[1500px] mx-auto flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <button onClick={() => navigate('/automoveis')} className="btn-ghost !p-0">
                        <FaArrowLeft size={10} /> Voltar ao stock
                    </button>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/25">
                        <button type="button" onClick={() => navigate('/')} className="hover:text-red bg-transparent border-0 cursor-pointer text-inherit uppercase tracking-[0.2em]">{SITE_NAME}</button>
                        <span className="text-red/40">/</span>
                        <button type="button" onClick={() => navigate('/automoveis')} className="hover:text-red bg-transparent border-0 cursor-pointer text-inherit uppercase tracking-[0.2em]">Stock</button>
                        <span className="text-red/40">/</span>
                        <span className="text-white/50 truncate max-w-[200px]">{car.titulo}</span>
                    </div>
                </div>
            </div>

            <section className="px-5 lg:px-12 py-10 lg:py-14 ajp-section-alt">
                <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    <div className="lg:col-span-7 space-y-6">
                        <div className="relative border border-white/[0.06] bg-[#0a0a0a] overflow-hidden">
                            {car.instagramShortcode ? (
                                <div className="relative w-full min-h-[360px] sm:min-h-[480px] lg:min-h-[600px]">
                                    <iframe
                                        src={`https://www.instagram.com/p/${car.instagramShortcode}/embed/captioned/`}
                                        className="absolute inset-0 w-full h-full border-0 pointer-events-none"
                                        scrolling="no"
                                        allowtransparency="true"
                                        allow="encrypted-media"
                                        title={car.titulo}
                                    />
                                </div>
                            ) : car.thumb_url ? (
                                <img src={car.thumb_url} alt={car.titulo} className="w-full aspect-[4/3] object-cover" />
                            ) : (
                                <div className="aspect-[4/3] bg-[#111]" />
                            )}
                        </div>

                        {specs.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/[0.06]">
                                {specs.map((spec) => (
                                    <div key={spec.label} className="bg-[#0a0a0a] p-4 sm:p-5">
                                        <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.2em] text-white/25 mb-2">
                                            <spec.icon size={10} className="text-red" />
                                            {spec.label}
                                        </div>
                                        <p className="text-sm font-semibold text-white">{spec.value}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {car.informacoes_extra && (
                            <div className="border border-white/[0.06] bg-[#0a0a0a] p-6 sm:p-8">
                                <p className="section-tag mb-4">Sobre este veículo</p>
                                <p className="text-white/45 text-sm leading-relaxed">{car.informacoes_extra}</p>
                            </div>
                        )}

                        <a
                            href={instagramUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30 hover:text-red transition-colors"
                        >
                            <FaExternalLinkAlt size={10} />
                            Ver publicação original no Instagram
                        </a>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28 space-y-6">
                            <div>
                                <span className={`inline-block px-3 py-1 border text-[10px] font-bold uppercase tracking-[0.2em] mb-4 ${statusClass}`}>
                                    {statusLabel}
                                </span>
                                <h1 className="font-display text-4xl sm:text-5xl text-white tracking-wide leading-none mb-2">
                                    {car.titulo}
                                </h1>
                                {car.marca && (
                                    <p className="text-white/30 text-sm uppercase tracking-widest">{car.marca}</p>
                                )}
                            </div>

                            <div className="border border-white/[0.06] p-6 sm:p-8 bg-[#0a0a0a]">
                                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/30 mb-2">Preço</p>
                                <p className="font-display text-5xl sm:text-6xl text-red tracking-wide">
                                    {formatPrice(car.preco)}
                                </p>
                            </div>

                            <div className="space-y-3">
                                <a
                                    href={`${SOCIAL_LINKS.whatsapp}?text=${contactMessage}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-primary w-full justify-center"
                                >
                                    <FaInstagram size={14} />
                                    Tenho interesse — Contactar
                                </a>
                                <a
                                    href={instagramUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-secondary w-full justify-center"
                                >
                                    <FaInstagram size={12} />
                                    Ver no Instagram
                                </a>
                            </div>

                            <p className="text-white/25 text-xs leading-relaxed">
                                Ref. {car.id} — Dados de exemplo até ligação completa à ficha técnica. Contacta-nos para confirmar equipamento e histórico.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CarDetailsPage;
